if (!customElements.get('metafield-range')) {
  class MetafieldRange extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.minNumberInput = this.querySelector('[data-mf-role="min-number"]');
      this.maxNumberInput = this.querySelector('[data-mf-role="max-number"]');
      this.minSliderInput = this.querySelector('[data-mf-role="min-slider"]');
      this.maxSliderInput = this.querySelector('[data-mf-role="max-slider"]');
      this.checkboxes = Array.from(this.querySelectorAll('[data-mf-value]'));

      this.absMin = Number(this.minSliderInput.min);
      this.absMax = Number(this.maxSliderInput.max);

      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
    }

    /**
     * 'input' = preview only (slider drag feedback). Checkboxes are not touched and
     * no reload happens until commit ('change'), so in-progress edits are never destroyed.
     * Number inputs are deliberately left alone while focused: rewriting them here
     * would eat the user's keystrokes.
     * @param {object} evt - Event object.
     */
    handleInput(evt) {
      if (evt.target === this.minSliderInput) {
        if (Number(this.minSliderInput.value) > Number(this.maxSliderInput.value)) {
          this.maxSliderInput.value = this.minSliderInput.value;
        }
        this.minNumberInput.value = this.minSliderInput.value;
      } else if (evt.target === this.maxSliderInput) {
        if (Number(this.maxSliderInput.value) < Number(this.minSliderInput.value)) {
          this.minSliderInput.value = this.maxSliderInput.value;
        }
        this.maxNumberInput.value = this.maxSliderInput.value;
      }
    }

    /**
     * 'change' = commit: slider released, or number input blurred/Enter pressed.
     * Runs before the facet-filters listener on the same bubbling event, so the
     * checkboxes are always in their final state when the form is serialized.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (!evt.target.matches('input[type="range"], input[type="number"]')) return;

      // A bound explicitly typed/stepped into a number input is written back even
      // when it equals the absolute bound — blanking it would visually undo the
      // user's entry (spinner from blank, typed exact floor/ceiling).
      const minTyped = evt.target === this.minNumberInput && this.minNumberInput.value.trim() !== '';
      const maxTyped = evt.target === this.maxNumberInput && this.maxNumberInput.value.trim() !== '';

      let minVal = this.parseNum(this.minNumberInput.value, this.absMin);
      let maxVal = this.parseNum(this.maxNumberInput.value, this.absMax);

      if (evt.target === this.minSliderInput) minVal = Number(this.minSliderInput.value);
      if (evt.target === this.maxSliderInput) maxVal = Number(this.maxSliderInput.value);

      minVal = Math.min(Math.max(minVal, this.absMin), this.absMax);
      maxVal = Math.min(Math.max(maxVal, this.absMin), this.absMax);

      if (minVal > maxVal) {
        if (evt.target === this.maxSliderInput || evt.target === this.maxNumberInput) {
          minVal = maxVal;
        } else {
          maxVal = minVal;
        }
      }

      this.minSliderInput.value = minVal;
      this.maxSliderInput.value = maxVal;
      this.minNumberInput.value = minVal <= this.absMin && !minTyped ? '' : minVal;
      this.maxNumberInput.value = maxVal >= this.absMax && !maxTyped ? '' : maxVal;

      this.updateCheckboxes(minVal, maxVal);
    }

    /**
     * Checks every hidden checkbox whose value falls inside [minVal, maxVal].
     * Bounds come from the committed values, not the (step-quantized) sliders,
     * so 2-decimal metafield values at the boundary are never dropped.
     * @param {number} minVal - Committed lower bound.
     * @param {number} maxVal - Committed upper bound.
     */
    updateCheckboxes(minVal, maxVal) {
      const isFullRange = minVal <= this.absMin && maxVal >= this.absMax;
      const epsilon = 0.001;

      this.checkboxes.forEach((cb) => {
        const v = Number(cb.dataset.mfValue);
        cb.checked = !isFullRange && v >= minVal - epsilon && v <= maxVal + epsilon;
      });
    }

    /**
     * Parses a number input value; blank or invalid input yields the fallback
     * (Number('') === 0, which is why a plain Number() call is not enough).
     * @param {string} value - Raw input value.
     * @param {number} fallback - Value to use when the field is blank/invalid.
     * @returns {number}
     */
    parseNum(value, fallback) {
      if (value == null || String(value).trim() === '') return fallback;
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    }
  }

  customElements.define('metafield-range', MetafieldRange);
}
